<template>
  <div class="exam-history-page">
    <div class="page-header">
      <div class="header-content">
        <button class="btn-back" @click="$router.back()">
          ← 返回
        </button>
        <h1>考试历史记录</h1>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>正在加载考试记录...</p>
    </div>

    <div v-else-if="errorMessage" class="error-container">
      <p>{{ errorMessage }}</p>
    </div>

    <div v-else class="history-container">
      <div class="filter-bar">
        <div class="filter-item">
          <label>课程筛选：</label>
          <select v-model="filterCourseId" @change="loadExamRecords">
            <option value="">全部课程</option>
            <option v-for="course in courseList" :key="course.id" :value="course.id">
              {{ course.title }}
            </option>
          </select>
        </div>
        <div class="filter-item">
          <label>状态筛选：</label>
          <select v-model="filterStatus" @change="loadExamRecords">
            <option value="">全部状态</option>
            <option value="passed">已通过</option>
            <option value="failed">未通过</option>
          </select>
        </div>
      </div>

      <div v-if="examRecords.length === 0" class="empty-state">
        <div class="empty-icon">📝</div>
        <p>暂无考试记录</p>
      </div>

      <div v-else class="exam-list">
        <div
          v-for="record in examRecords"
          :key="record.id"
          class="exam-card"
          @click="viewExamDetail(record)"
        >
          <div class="exam-card__header">
            <div class="exam-info">
              <h3>{{ record.exam_title || record.target_title }}</h3>
              <p class="exam-course">{{ record.course_title }}</p>
            </div>
            <div :class="['exam-status', record.is_passed ? 'passed' : 'failed']">
              {{ record.is_passed ? '✓ 已通过' : '✗ 未通过' }}
            </div>
          </div>

          <div class="exam-card__body">
            <div class="exam-meta">
              <div class="meta-item">
                <span class="meta-label">得分：</span>
                <span class="meta-value score">{{ record.score }} 分</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">及格分：</span>
                <span class="meta-value">{{ record.pass_score }} 分</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">用时：</span>
                <span class="meta-value">{{ formatDuration(record.duration) }}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">考试时间：</span>
                <span class="meta-value">{{ formatDateTime(record.created_at) }}</span>
              </div>
            </div>
          </div>

          <div class="exam-card__footer">
            <button class="btn-view" @click.stop="viewExamDetail(record)">
              查看详情
            </button>
            <button
              v-if="!record.is_passed && record.can_retry"
              class="btn-retry"
              @click.stop="retryExam(record)"
            >
              重新考试
            </button>
          </div>
        </div>
      </div>

      <div v-if="total > pageSize" class="pagination">
        <button
          class="btn-page"
          :disabled="page === 1"
          @click="changePage(page - 1)"
        >
          上一页
        </button>
        <span class="page-info">第 {{ page }} / {{ totalPages }} 页</span>
        <button
          class="btn-page"
          :disabled="page >= totalPages"
          @click="changePage(page + 1)"
        >
          下一页
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchExamRecords } from '@/api/courseProcess'

export default {
  name: 'ExamHistory',
  data() {
    return {
      loading: true,
      errorMessage: '',
      examRecords: [],
      courseList: [],
      filterCourseId: '',
      filterStatus: '',
      page: 1,
      pageSize: 10,
      total: 0
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  created() {
    this.loadExamRecords()
  },
  methods: {
    async loadExamRecords() {
      try {
        this.loading = true
        this.errorMessage = ''

        const params = {
          page: this.page,
          page_size: this.pageSize
        }

        if (this.filterCourseId) {
          params.course_id = this.filterCourseId
        }

        if (this.filterStatus) {
          params.status = this.filterStatus
        }

        const res = await fetchExamRecords(params)
        this.examRecords = res.data || []
        this.total = res.total || 0

        if (res.courses) {
          this.courseList = res.courses
        }
      } catch (error) {
        this.errorMessage = error.message || '加载考试记录失败'
      } finally {
        this.loading = false
      }
    },

    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.page = newPage
        this.loadExamRecords()
      }
    },

    viewExamDetail(record) {
      this.$router.push({
        name: 'exam-result',
        params: { id: record.id },
        query: {
          course_id: record.course_id,
          process_id: record.process_id
        }
      })
    },

    retryExam(record) {
      this.$router.push({
        name: 'exam',
        params: { id: record.process_id },
        query: { course_id: record.course_id }
      })
    },

    formatDuration(seconds) {
      if (!seconds) return '-'
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${minutes}分${secs}秒`
    },

    formatDateTime(datetime) {
      if (!datetime) return '-'
      const date = new Date(datetime)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.exam-history-page {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.page-header {
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  padding: 20px 40px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.btn-back {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-back:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e0e0e0;
  border-top-color: #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.history-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  width: 100%;
}

.filter-bar {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-item label {
  font-size: 14px;
  color: #666;
  white-space: nowrap;
}

.filter-item select {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 8px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.empty-state p {
  font-size: 16px;
  color: #999;
}

.exam-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.exam-card {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s;
}

.exam-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.exam-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.exam-info h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.exam-course {
  margin: 0;
  font-size: 14px;
  color: #999;
}

.exam-status {
  padding: 6px 16px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.exam-status.passed {
  background: #f6ffed;
  color: #52c41a;
}

.exam-status.failed {
  background: #fff2e8;
  color: #fa8c16;
}

.exam-card__body {
  margin-bottom: 20px;
}

.exam-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.meta-label {
  font-size: 14px;
  color: #999;
}

.meta-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.meta-value.score {
  font-size: 18px;
  color: #1890ff;
  font-weight: 600;
}

.exam-card__footer {
  display: flex;
  gap: 12px;
}

.btn-view,
.btn-retry {
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-view {
  background: #1890ff;
  color: #fff;
}

.btn-view:hover {
  background: #40a9ff;
}

.btn-retry {
  background: #fff;
  color: #1890ff;
  border: 1px solid #1890ff;
}

.btn-retry:hover {
  background: #e6f7ff;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 40px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
}

.btn-page {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-page:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

.btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}
</style>
